import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button, Tooltip } from "@mantine/core";
import TextareaAutosize from "react-textarea-autosize";
import { Refresh, Copy, X, Maximize, Minimize } from "tabler-icons-react";
import { useChat } from "./contexts/ChatContext";
import { useChatService } from "./services/ChatService";

const PopupChat = () => {
  const chat = useChat();
  const chatService = useChatService();

  // Popup specific state
  const [position, setPosition] = useState(chat.popupPosition);
  const [size, setSize] = useState(chat.popupSize);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isMaximized, setIsMaximized] = useState(false);
  const prevPositionRef = useRef(null);
  const prevSizeRef = useRef(null);

  // Refs
  const popupRef = useRef(null);
  const messagesEndRef = useRef(null);
  const latestMessageRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize chat service with context values when popup opens
  useEffect(() => {
    if (chat.conversationID) {
      chatService.initializeChat(chat.conversationID, chat.initialSuggestions, chat.fileIds);
    }
  }, [chat.conversationID, chat.initialSuggestions, chat.fileIds, chatService]);

  // Scroll to latest message
  useEffect(() => {
    if (latestMessageRef.current) {
      latestMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [chat.messages]);

  // Handle drag functionality
  const handleMouseDown = useCallback(
    (e) => {
      // Only allow dragging from the header area, but not from buttons
      if (
        e.target.closest("button") ||
        !e.target.closest(".popup-header-drag")
      ) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    },
    [position]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;

      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      setPosition({ x: newX, y: newY });
    },
    [isDragging, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
  }, []);

  // Handle resize functionality
  const handleResizeMouseDown = useCallback((e) => {
    e.stopPropagation();
    setIsResizing(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
    });
  }, []);

  const handleResizeMouseMove = useCallback(
    (e) => {
      if (!isResizing) return;

      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      const newWidth = Math.max(300, Math.min(800, size.width + deltaX));
      const newHeight = Math.max(400, Math.min(800, size.height + deltaY));

      setSize({ width: newWidth, height: newHeight });
      setDragStart({ x: e.clientX, y: e.clientY });
    },
    [isResizing, dragStart, size]
  );

  // Global mouse events
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (isDragging) {
        handleMouseMove(e);
      } else if (isResizing) {
        handleResizeMouseMove(e);
      }
    };

    const handleGlobalMouseUp = () => {
      if (isDragging || isResizing) {
        handleMouseUp();
      }
    };

    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleGlobalMouseMove, {
        passive: false,
      });
      document.addEventListener("mouseup", handleGlobalMouseUp, {
        passive: false,
      });
    }

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [
    isDragging,
    isResizing,
    handleMouseMove,
    handleResizeMouseMove,
    handleMouseUp,
  ]);

  // Handle input
  const handleSend = async (questionText = chat.inputDraft) => {
    await chatService.sendMessage(questionText);
  };

  const handleSuggestionClick = async (suggestion, conversationID) => {
    await chatService.handleSuggestionClick(suggestion, conversationID);
  };

  const handleCopyMessage = (text) => {
    chatService.copyMessage(text);
  };

  const handleRerunMessage = async (previousQuestion, conversationID) => {
    await chatService.rerunMessage(previousQuestion, conversationID);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = () => {
    chat.closeChat();
  };

  const toggleMaximize = () => {
    // Toggle maximize: if going to maximize, save current size/position and compute 4:3 centered size
    if (!isMaximized) {
      prevPositionRef.current = position;
      prevSizeRef.current = size;

      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Start with 90% of viewport width
      let width = Math.round(vw * 0.9);
      let height = Math.round((width * 3) / 4);
      // If height too tall, constrain by 90% of viewport height
      if (height > Math.round(vh * 0.9)) {
        height = Math.round(vh * 0.9);
        width = Math.round((height * 4) / 3);
      }
      const left = Math.round((vw - width) / 2);
      const top = Math.round((vh - height) / 2);

      setSize({ width, height });
      setPosition({ x: left, y: top });
      setIsMaximized(true);
    } else {
      // Restore previous
      if (prevPositionRef.current) setPosition(prevPositionRef.current);
      if (prevSizeRef.current) setSize(prevSizeRef.current);
      setIsMaximized(false);
    }
  };

  // Get latest AI message ID for rerun button state
  const latestAIMessageId = chatService.getLatestAIMessageId();

  const popupHeight = size.height;

  return (
    <div
      ref={popupRef}
      style={{
        position: "fixed",
        left: position.x,
        top: position.y,
        width: size.width,
        height: popupHeight,
        background: "#fff",
        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        borderRadius: "12px",
        zIndex: 10000,
        display: "flex",
        flexDirection: "column",
        fontFamily: "Lexend",
        border: "1px solid #e5e7eb",
        overflow: "hidden",
      }}
    >
      {/* Header - Draggable */}
      <div
        className="popup-header-drag"
        style={{
          display: "flex",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: isMaximized ? "none" : "1px solid #eee",
          background: "#f8fafc",
          borderRadius: "12px 12px 0 0",
          userSelect: "none",
          cursor: isDragging ? "grabbing" : "grab",
        }}
        onMouseDown={handleMouseDown}
      >
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: "orange",
            marginRight: 8,
          }}
        />
        <div
          style={{
            fontWeight: 600,
            fontSize: 14,
            flex: 1,
            display: "flex",
            alignItems: "center",
          }}
        >
          lawyal.ai
        </div>

        <Button
          variant="subtle"
          size="xs"
          color="gray"
          onClick={toggleMaximize}
          style={{ marginRight: 4, padding: "4px", minWidth: "auto" }}
        >
          {isMaximized ? <Minimize size={14} /> : <Maximize size={14} />}
        </Button>

        <Button
          variant="subtle"
          size="xs"
          color="gray"
          onClick={handleClose}
          style={{ padding: "4px", minWidth: "auto" }}
        >
          <X size={14} />
        </Button>
      </div>

      <>
        {/* Messages */}
        <div
          className="popup-content"
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 12,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {chat.messages.map((msg, index) => (
            <React.Fragment key={msg.id}>
              <div
                ref={
                  index === chat.messages.length - 1 ? latestMessageRef : null
                }
                className={`
                    ${
                      msg.sender === "user"
                        ? "self-end bg-indigo-100"
                        : "self-start bg-gray-50"
                    }
                    rounded-lg p-3 mb-2 max-w-[85%] ${
                      msg.sender === "user" ? "whitespace-pre-line" : ""
                    }
                  `}
                style={{
                  lineHeight: "1.5",
                  fontSize: "13px",
                  fontFamily: "Lexend, sans-serif",
                }}
              >
                {msg.isLoading ? (
                  <div className="flex items-center space-x-1">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                ) : msg.sender === "ai" ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: msg.text }}
                    className="prose prose-sm max-w-none ai-message"
                  />
                ) : (
                  msg.text
                )}
              </div>

              {/* Action buttons for AI messages */}
              {msg.sender === "ai" && !msg.isLoading && (
                <div className="flex gap-1 mb-3 ml-1">
                  <Tooltip label="Copy message">
                    <Button
                      variant="subtle"
                      size="xs"
                      color="gray"
                      onClick={() => handleCopyMessage(msg.text)}
                      styles={{
                        root: {
                          height: "24px",
                          width: "24px",
                          padding: "0",
                          minWidth: "unset",
                        },
                      }}
                    >
                      <Copy size={12} />
                    </Button>
                  </Tooltip>

                  <Tooltip label="Regenerate response">
                    <Button
                      variant="subtle"
                      size="xs"
                      color="gray"
                      onClick={() => {
                        const previousUserMsg = chat.messages
                          .slice(0, index)
                          .reverse()
                          .find((m) => m.sender === "user");
                        if (previousUserMsg) {
                          handleRerunMessage(
                            previousUserMsg.text,
                            msg.conversationID
                          );
                        }
                      }}
                      disabled={chat.loading || msg.id !== latestAIMessageId}
                      styles={{
                        root: {
                          height: "24px",
                          width: "24px",
                          padding: "0",
                          minWidth: "unset",
                        },
                      }}
                    >
                      <Refresh size={12} />
                    </Button>
                  </Tooltip>
                </div>
              )}

              {/* Document References */}
              {msg.sender === "ai" &&
                !msg.isLoading &&
                msg.documentReferences &&
                msg.documentReferences.length > 0 && (
                  <div
                    style={{
                      marginTop: 8,
                      marginBottom: 12,
                      padding: 10,
                      backgroundColor: "#f9fafb",
                      borderRadius: 6,
                      fontSize: "11px",
                      maxWidth: "85%",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        marginBottom: 6,
                        color: "#6b7280",
                      }}
                    >
                      📄 Sources:
                    </div>
                    {msg.documentReferences.map((ref, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: 6,
                          marginBottom: 4,
                          borderLeft: "2px solid #3b82f6",
                          paddingLeft: 8,
                        }}
                      >
                        <div style={{ fontWeight: 500, color: "#374151" }}>
                          {ref.file_name || `Document ${ref.file_id}`}
                        </div>
                        {ref.excerpt && (
                          <div
                            style={{
                              color: "#6b7280",
                              marginTop: 3,
                              fontStyle: "italic",
                            }}
                          >
                            "{ref.excerpt}"
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

              {/* Suggestions */}
              <div className="max-w-[85%]">
                {msg.sender === "ai" &&
                  !msg.isLoading &&
                  msg.suggestions &&
                  msg.suggestions.length > 0 && (
                    <div
                      style={{
                        marginTop: 6,
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                      className="mb-6"
                    >
                      {msg.suggestions.map((suggestion, idx) => (
                        <Button
                          key={idx}
                          variant="outline"
                          size="xs"
                          className="p-2 h-auto"
                          styles={{
                            label: {
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              textAlign: "left",
                              fontSize: "12px",
                            },
                          }}
                          onClick={() =>
                            handleSuggestionClick(
                              suggestion,
                              msg.conversationID
                            )
                          }
                          disabled={chat.loading}
                          title={suggestion}
                        >
                          {suggestion}
                        </Button>
                      ))}
                    </div>
                  )}
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Input */}
        <div
          style={{
            padding: 12,
            borderTop: "1px solid #eee",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <TextareaAutosize
            ref={inputRef}
            className="no-resize"
            value={chat.inputDraft}
            onChange={(e) => chatService.updateInputDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your question..."
            minRows={2}
            maxRows={4}
            style={{
              flex: 1,
              borderRadius: 6,
              border: "1px solid #ddd",
              padding: 6,
              lineHeight: 1.3,
              fontSize: 12,
              fontFamily: "Lexend, sans-serif",
            }}
            disabled={chat.loading}
          />
          <Button
            onClick={handleSend}
            disabled={chatService.isSendDisabled()}
            size="xs"
            styles={{
              root: {
                background: "#61dafb",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "6px 12px",
                fontWeight: 600,
                fontSize: "12px",
                "&[data-disabled]": {
                  background: "#96def1ff",
                  opacity: 1,
                  color: "#fff",
                  cursor: "not-allowed",
                },
              },
            }}
          >
            Send
          </Button>
        </div>

        {chat.error && (
          <div
            style={{
              color: "red",
              textAlign: "center",
              padding: 8,
              fontSize: 12,
            }}
          >
            {chat.error}
          </div>
        )}
      </>

      {/* Resize handle */}
      {!isMaximized && (
        <div
          className="popup-resize-handle"
          style={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: 12,
            height: 12,
            cursor: "se-resize",
            background:
              "linear-gradient(-45deg, transparent 0%, transparent 30%, #ccc 30%, #ccc 40%, transparent 40%)",
          }}
          onMouseDown={handleResizeMouseDown}
        />
      )}

      {/* Styles */}
      <style jsx>{`
        .typing-indicator {
          display: flex;
          align-items: center;
          gap: 3px;
        }

        .typing-indicator span {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background-color: #6b7280;
          animation: typing 1.4s infinite ease-in-out;
        }

        .typing-indicator span:nth-child(1) {
          animation-delay: 0s;
        }

        .typing-indicator span:nth-child(2) {
          animation-delay: 0.2s;
        }

        .typing-indicator span:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes typing {
          0%,
          60%,
          100% {
            transform: translateY(0);
            opacity: 0.4;
          }
          30% {
            transform: translateY(-8px);
            opacity: 1;
          }
        }

        .prose {
          max-width: none;
          color: #1f2937;
          line-height: 1.6;
          font-size: 13px;
          font-family: "Lexend", sans-serif;
        }

        .prose p {
          margin: 0.5em 0;
        }

        .prose strong {
          font-weight: 600;
          color: #111827;
        }

        .prose ul,
        .prose ol {
          list-style: disc outside !important;
          padding-left: 1.2em !important;
          margin: 0.5em 0 !important;
        }

        .prose li {
          margin-bottom: 0.3em !important;
          line-height: 1.5;
        }

        .no-resize {
          resize: none !important;
          -webkit-resize: none !important;
        }
      `}</style>
    </div>
  );
};

export default PopupChat;
